import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

interface PendingProjectData {
  prompt: string;
  projectName: string;
}

interface PendingProjectContextType {
  pendingProject: PendingProjectData | null;
  setPendingProject: (data: PendingProjectData | null) => void;
  clearPendingProject: () => void;
}

const PendingProjectContext = createContext<PendingProjectContextType | undefined>(undefined);

export const usePendingProject = () => {
  const context = useContext(PendingProjectContext);
  if (!context) {
    throw new Error('usePendingProject must be used within a PendingProjectProvider');
  }
  return context;
};

interface PendingProjectProviderProps {
  children: React.ReactNode;
}

export const PendingProjectProvider: React.FC<PendingProjectProviderProps> = ({ children }) => {
  const [pendingProject, setPendingProjectState] = useState<PendingProjectData | null>(null);

  const setPendingProject = useCallback((data: PendingProjectData | null) => {
    setPendingProjectState(data);
    // Also store in sessionStorage to survive page navigation
    if (data) {
      sessionStorage.setItem('pendingProject', JSON.stringify(data));
    } else {
      sessionStorage.removeItem('pendingProject');
    }
  }, []);

  const clearPendingProject = useCallback(() => {
    setPendingProjectState(null);
    sessionStorage.removeItem('pendingProject');
  }, []);

  // Initialize from sessionStorage on mount
  React.useEffect(() => {
    const stored = sessionStorage.getItem('pendingProject');
    if (stored) {
      try {
        setPendingProjectState(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse pending project:', e);
      }
    }
  }, []);

  const value = useMemo(() => ({
    pendingProject,
    setPendingProject,
    clearPendingProject
  }), [pendingProject, setPendingProject, clearPendingProject]);

  return (
    <PendingProjectContext.Provider value={value}>
      {children}
    </PendingProjectContext.Provider>
  );
};
